import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  EnvelopeSimple,
  WhatsappLogo,
  MapPin,
  ArrowRight,
  CheckCircle,
} from "@phosphor-icons/react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WhatsAppFab from "../components/WhatsAppFab";
import { openWhatsapp } from "../lib/whatsapp";
import { submitLead } from "../lib/api";
import { toast } from "sonner";

const EXP_OPTIONS = ["Beginner", "1–3 yrs", "3–5 yrs", "5+ yrs"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    experience: "Beginner",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        experience: form.experience,
        message: (form.message.trim() || "[Contact page]"),
      });
      setDone(true);
      toast.success("Thanks! We'll reach out within 24 hours.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not send. Please WhatsApp us."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="contact-root"
      className="min-h-screen bg-[#F4F3ED] text-[#0A0A0A] font-body"
    >
      <Navbar />
      <main className="pt-28 lg:pt-32 pb-16">
        <section className="px-5 lg:px-10 max-w-[1320px] mx-auto">
          <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-3">
            <Link to="/" className="hover:text-[#0A0A0A]">
              Home
            </Link>{" "}
            / Contact
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.9] mb-5">
            Let's <span className="text-[#D9381E]">talk.</span>
          </h1>
          <p className="font-body text-base lg:text-lg text-[#4A4A4A] max-w-2xl mb-12">
            Questions about packages, batch dates, the ₹999 community or
            GoCharting? Drop a note or message us on WhatsApp — we reply within
            24 hours every working day.
          </p>
        </section>

        <section className="px-5 lg:px-10 max-w-[1320px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* LEFT — info */}
          <aside className="lg:col-span-5 space-y-5">
            <div
              data-testid="contact-info-locations"
              className="border border-[#0A0A0A] bg-[#0A0A0A] text-[#F4F3ED] p-7"
            >
              <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-3">
                Locations
              </div>
              <ul className="space-y-3">
                {["Trivandrum, Kerala", "Bengaluru, Karnataka"].map((c) => (
                  <li key={c} className="flex items-center gap-3 font-body text-base">
                    <MapPin size={20} weight="duotone" className="text-[#D9381E]" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border border-[#0A0A0A] bg-[#F4F3ED] p-7" data-testid="contact-info-direct">
              <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-3">
                Reach us
              </div>
              <ul className="space-y-3">
                <li>
                  <a
                    href="tel:+918129387138"
                    className="flex items-center gap-3 font-body text-base hover:text-[#D9381E]"
                  >
                    <Phone size={18} weight="duotone" className="text-[#D9381E]" />
                    +91 81293 87138
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+918129387137"
                    className="flex items-center gap-3 font-body text-base hover:text-[#D9381E]"
                  >
                    <Phone size={18} weight="duotone" className="text-[#D9381E]" />
                    +91 81293 87137
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+917356326453"
                    className="flex items-center gap-3 font-body text-base hover:text-[#D9381E]"
                  >
                    <Phone size={18} weight="duotone" className="text-[#D9381E]" />
                    +91 73563 26453
                    <span className="font-mono-acc text-[10px] tracking-[0.2em] text-[#4A4A4A] uppercase ml-1">
                      (Account opening)
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:office.greateagle@gmail.com"
                    className="flex items-center gap-3 font-body text-sm hover:text-[#D9381E] break-all"
                  >
                    <EnvelopeSimple size={18} weight="duotone" className="text-[#D9381E]" />
                    office.greateagle@gmail.com
                  </a>
                </li>
              </ul>
              <button
                onClick={() => openWhatsapp()}
                data-testid="contact-whatsapp-btn"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-[#0A0A0A] px-5 py-3 font-body text-sm uppercase tracking-[0.22em] font-bold hover:bg-[#1ebd5b] transition-colors"
              >
                <WhatsappLogo size={18} weight="fill" /> Chat on WhatsApp
              </button>
            </div>
          </aside>

          {/* RIGHT — form */}
          <div className="lg:col-span-7 border border-[#0A0A0A] bg-[#F4F3ED] p-7 lg:p-10">
            {!done ? (
              <>
                <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-2">
                  Contact form
                </div>
                <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mb-2">
                  Drop us a note.
                </h2>
                <p className="font-body text-sm text-[#4A4A4A] mb-6">
                  Fill this and our team will reach out within 24 hours.
                </p>
                <form
                  onSubmit={onSubmit}
                  className="space-y-4"
                  data-testid="contact-form"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
                        Full Name *
                      </label>
                      <input
                        data-testid="contact-name"
                        required
                        name="name"
                        value={form.name}
                        onChange={onChange}
                        className="w-full px-4 py-3 bg-[#F4F3ED] border border-[#0A0A0A] focus:outline-none focus:border-[#D9381E] font-body text-sm rounded-none"
                      />
                    </div>
                    <div>
                      <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
                        Phone *
                      </label>
                      <input
                        data-testid="contact-phone"
                        required
                        name="phone"
                        value={form.phone}
                        onChange={onChange}
                        placeholder="+91 ••••• •••••"
                        className="w-full px-4 py-3 bg-[#F4F3ED] border border-[#0A0A0A] focus:outline-none focus:border-[#D9381E] font-body text-sm rounded-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
                      Email *
                    </label>
                    <input
                      data-testid="contact-email"
                      required
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={onChange}
                      className="w-full px-4 py-3 bg-[#F4F3ED] border border-[#0A0A0A] focus:outline-none focus:border-[#D9381E] font-body text-sm rounded-none"
                    />
                  </div>
                  <div>
                    <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
                      Trading Experience
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {EXP_OPTIONS.map((o) => (
                        <button
                          key={o}
                          type="button"
                          data-testid={`contact-exp-${o}`}
                          onClick={() =>
                            setForm((p) => ({ ...p, experience: o }))
                          }
                          className={`px-3 py-2.5 border text-xs font-body tracking-[0.15em] uppercase transition-colors ${
                            form.experience === o
                              ? "bg-[#0A0A0A] text-[#F4F3ED] border-[#0A0A0A]"
                              : "bg-[#F4F3ED] text-[#0A0A0A] border-[#0A0A0A] hover:bg-[#EAE8DF]"
                          }`}
                        >
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] block mb-2">
                      Message
                    </label>
                    <textarea
                      data-testid="contact-message"
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      rows={4}
                      placeholder="What would you like to discuss?"
                      className="w-full px-4 py-3 bg-[#F4F3ED] border border-[#0A0A0A] focus:outline-none focus:border-[#D9381E] font-body text-sm rounded-none resize-none"
                    />
                  </div>
                  <button
                    data-testid="contact-submit"
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#0A0A0A] text-[#F4F3ED] py-4 font-body text-sm uppercase tracking-[0.22em] hover:bg-[#D9381E] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-3"
                  >
                    {loading ? "Sending…" : "Send message"}
                    {!loading && <ArrowRight weight="bold" size={16} />}
                  </button>
                </form>
              </>
            ) : (
              <div
                data-testid="contact-success"
                className="flex flex-col items-center text-center py-8"
              >
                <CheckCircle
                  size={64}
                  weight="duotone"
                  className="text-[#D9381E] mb-5"
                />
                <h3 className="font-display font-black text-3xl tracking-tighter mb-3">
                  Message sent.
                </h3>
                <p className="font-body text-[#4A4A4A] max-w-md mb-6">
                  Our team will reach out to{" "}
                  <span className="font-semibold text-[#0A0A0A]">
                    {form.email}
                  </span>{" "}
                  within 24 hours.
                </p>
                <button
                  onClick={() => openWhatsapp()}
                  className="inline-flex items-center gap-3 bg-[#25D366] text-[#0A0A0A] px-6 py-3 font-body text-sm uppercase tracking-[0.22em] font-bold cursor-pointer"
                >
                  <WhatsappLogo size={18} weight="fill" />
                  Talk to us now
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
